/**
 * Letter-first onboarding — "The people you trust."
 *
 * A veneer over the EXISTING invitation flow, never a replacement: the
 * add buttons open the proven AddSuccessorScreen / AddKeyHolderScreen
 * (client-hashed validation answer, scoped magic-link invitations),
 * prefilled from this letter's recipients where possible. Explained in
 * the user's own words, successors plural.
 */
import { useFocusEffect, useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import {
  letterToLovedOnesService,
  DeliveryCandidate,
  LetterRecipientRec,
} from '../../services/letterToLovedOnesInfo';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('LetterPeople');

type PeopleRouteProp = RouteProp<{ params: { letterId?: string } }, 'params'>;

type Tab = 'successor' | 'keyholder';

const TAB_EXPLAINER: Record<Tab, string> = {
  successor:
    'People you trust to reach everything once an end-of-life event is verified. They help carry out your wishes. You can change who they are whenever you like.',
  keyholder:
    'Someone you want close, but not all the way in. A Keyholder only opens the specific parts you hand them — like this letter.',
};

export const LetterPeopleScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<PeopleRouteProp>();
  const letterId = route.params?.letterId;

  // Letter delivery is keyholders-only; successors are introduced at
  // Level 2 with the vault. The tab machinery stays for that day.
  const [tab] = useState<Tab>('keyholder');
  const [successors, setSuccessors] = useState<DeliveryCandidate[]>([]);
  const [keyholders, setKeyholders] = useState<DeliveryCandidate[]>([]);
  const [recipients, setRecipients] = useState<LetterRecipientRec[]>([]);
  const [loading, setLoading] = useState(true);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const load = async () => {
        try {
          const [people, letter] = await Promise.all([
            letterToLovedOnesService.getDeliveryCandidates(),
            letterId
              ? letterToLovedOnesService.getLetterToLovedOnesById(letterId)
              : Promise.resolve(null),
          ]);
          if (cancelled) return;
          setSuccessors(people.successors || []);
          setKeyholders(people.keyholders || []);
          setRecipients(letter?.recipients || []);
        } catch (error) {
          logger.error('Failed to load people:', error);
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [letterId]),
  );

  const list = tab === 'successor' ? successors : keyholders;

  const handleAdd = (prefillName?: string) => {
    // Reuse the existing, security-load-bearing add flows with the
    // letter recipient's name prefilled — the user completes email,
    // phone, and the validation question there.
    const prefilledData = prefillName ? { firstName: prefillName } : undefined;
    if (tab === 'successor') {
      navigation.navigate('Profile', {
        screen: 'AddSuccessor',
        params: prefilledData ? { prefilledData } : undefined,
      });
    } else {
      navigation.navigate('Profile', {
        screen: 'AddKeyholder',
        params: prefilledData ? { prefilledData } : undefined,
      });
    }
  };

  const displayName = (c: DeliveryCandidate) =>
    [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email;

  // Letter recipients — one-tap prefill into the add flows.
  const suggestions = recipients.filter((r) => r.name && r.name.trim().length > 0);

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <View style={styles.headerSpacer} />
        <Text style={styles.headerTitle}>The people you trust</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        {loading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color="#29BFC0" size="large" />
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={styles.explainerCard}>
              <Text style={styles.explainerText}>{TAB_EXPLAINER[tab]}</Text>
            </View>

            {list.map((person) => (
              <View key={person.email} style={styles.personCard}>
                <View style={styles.personText}>
                  <Text style={styles.personName}>{displayName(person)}</Text>
                  <Text style={styles.personSub}>
                    {person.email}
                    {person.status ? ` · ${person.status}` : ''}
                  </Text>
                </View>
              </View>
            ))}
            {list.length === 0 && (
              <Text style={styles.emptyText}>
                {tab === 'successor'
                  ? 'No successors yet. Your letter is already safe — this can wait.'
                  : 'No keyholders yet. Your letter is already safe — this can wait.'}
              </Text>
            )}

            {suggestions.length > 0 && (
              <>
                <Text style={styles.suggestLabel}>FROM THIS LETTER</Text>
                {suggestions.map((r) => (
                  <TouchableOpacity
                    key={r.name}
                    style={styles.suggestCard}
                    onPress={() => handleAdd(r.name)}
                    accessibilityRole="button"
                    accessibilityLabel={`Make ${r.name} a ${tab}`}
                  >
                    <Text style={styles.suggestName}>{r.name}</Text>
                    <Text style={styles.suggestAction}>
                      Make {tab === 'successor' ? 'a Successor' : 'a Keyholder'} →
                    </Text>
                  </TouchableOpacity>
                ))}
              </>
            )}

            <TouchableOpacity
              style={styles.addButton}
              onPress={() => handleAdd()}
              accessibilityRole="button"
              accessibilityLabel={tab === 'successor' ? 'Name a Successor' : 'Add a Keyholder'}
            >
              <Text style={styles.addButtonText}>
                {tab === 'successor' ? '+ Name a Successor' : '+ Add a Keyholder'}
              </Text>
            </TouchableOpacity>

            <Text style={styles.footnote}>
              One question only you two would know is part of adding someone —
              it's how we make sure the right person opens this.
            </Text>

            <TouchableOpacity
              style={styles.doneButton}
              onPress={() => navigation.goBack()}
              accessibilityRole="button"
              accessibilityLabel="Done"
              accessibilityHint="Double tap to return to delivery setup"
            >
              <Text style={styles.doneButtonText}>Done</Text>
            </TouchableOpacity>

            <View style={styles.bottomPadding} />
          </ScrollView>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(16),
    paddingHorizontal: ms(16),
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabs: {
    flexDirection: 'row',
    backgroundColor: '#E6EDF0',
    borderRadius: ms(25),
    padding: ms(4),
    marginBottom: ms(14),
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: ms(9),
    borderRadius: ms(25),
  },
  tabOn: {
    backgroundColor: '#FFFFFF',
  },
  tabText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#6B7280',
  },
  tabTextOn: {
    color: '#1C2C4F',
  },
  explainerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#C7E9EB',
    padding: ms(14),
    marginBottom: ms(12),
  },
  explainerText: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#1C2C4F',
  },
  personCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(14),
    marginBottom: ms(8),
  },
  personText: {
    flex: 1,
  },
  personName: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  personSub: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  emptyText: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
    marginBottom: ms(10),
  },
  suggestLabel: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#A9A9A9',
    letterSpacing: 1,
    marginTop: ms(8),
    marginBottom: ms(8),
  },
  suggestCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#C7E9EB',
    padding: ms(14),
    marginBottom: ms(8),
  },
  suggestName: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  suggestAction: {
    fontSize: ms(13, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  addButton: {
    alignItems: 'center',
    paddingVertical: ms(12),
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    marginTop: ms(6),
  },
  doneButton: {
    alignItems: 'center',
    paddingVertical: ms(15),
    borderRadius: ms(25),
    backgroundColor: '#29BFC0',
    marginTop: ms(16),
  },
  doneButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  addButtonText: {
    color: '#29BFC0',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  footnote: {
    fontSize: ms(12, 0.3),
    lineHeight: ms(17, 0.3),
    color: '#A9A9A9',
    textAlign: 'center',
    marginTop: ms(12),
  },
  bottomPadding: {
    height: s(40),
  },
});

export default LetterPeopleScreen;
